/**
 * The orders the Comments panel can list comments in, and the one function that applies them.
 *
 * One module for the option set, its labels and the comparison, read by the desktop menu
 * (`SortButton`), the mobile drawer (`MobileSortDrawer`) and the list itself — so an option can
 * be added, renamed or reworded in one place and every surface follows. The same arrangement as
 * `pages/Libraries/utils/sortOptions.ts`.
 *
 * Only top-level comments are reordered. Replies stay in the order they were written under each
 * one whatever the sort: a thread is a conversation, and reordering one would make an answer read
 * before its question.
 *
 * The store is untouched by any of this and keeps comments in timecode order, because the
 * scrub-bar and filmstrip markers are drawn from it and a marker's place is its timecode, not the
 * reader's choice of list order.
 */

import { compareByAnchor, type MediaCommentAnchor } from './commentAnchor';
import { authorLabel, isOwnWriting } from './commentAuthor';

export type CommentSort = 'timecode' | 'oldest' | 'newest' | 'commenter';

/** The list's order until the reader picks another: it follows the scrub bar beside it. */
export const DEFAULT_COMMENT_SORT: CommentSort = 'timecode';

/** Grouped as the menus show them, a divider between groups. */
export const COMMENT_SORT_GROUPS: { value: CommentSort; label: string }[][] = [
  [{ value: 'timecode', label: 'Timecode' }],
  [
    { value: 'oldest', label: 'Oldest first' },
    { value: 'newest', label: 'Newest first' },
  ],
  [{ value: 'commenter', label: 'Commenter' }],
];

export const COMMENT_SORT_LABELS: Record<CommentSort, string> = Object.fromEntries(
  COMMENT_SORT_GROUPS.flat().map(({ value, label }) => [value, label]),
) as Record<CommentSort, string>;

/**
 * The options worth offering for this list: all of them, less `commenter` until a second person
 * has commented. With one author, sorting by commenter is timecode order under another name — a
 * choice that visibly does nothing reads as broken, so it is not offered until it can do
 * something. Groups left empty are dropped, so no menu shows a stray divider.
 */
export function availableCommentSortGroups(
  comments: readonly { author: { userId: string } }[],
): { value: CommentSort; label: string }[][] {
  const authors = new Set(comments.map((comment) => comment.author.userId));
  if (authors.size > 1) return COMMENT_SORT_GROUPS;
  return COMMENT_SORT_GROUPS.map((group) =>
    group.filter((option) => option.value !== 'commenter'),
  ).filter((group) => group.length > 0);
}

/**
 * The sort to actually apply: the reader's choice while it is on offer, the default otherwise.
 *
 * The choice itself is kept, not overwritten. A reader on Commenter whose only other commenter
 * deletes their note sees timecode order and a menu with Timecode checked — both true — and gets
 * Commenter back if a second person comments again, rather than finding their choice was quietly
 * replaced.
 */
export function effectiveCommentSort(
  sort: CommentSort,
  groups: readonly { value: CommentSort }[][],
): CommentSort {
  return groups.some((group) => group.some((option) => option.value === sort))
    ? sort
    : DEFAULT_COMMENT_SORT;
}

/**
 * A list sorted by commenter, cut into one section per person, for the headings that make that
 * sort legible — without them, grouping by person looks like any other order.
 *
 * Consecutive runs rather than a lookup: the input is already grouped by `sortComments`, and
 * cutting it where the author changes keeps each section in exactly the order it was sorted to.
 */
export function groupByAuthor<T extends { author: { userId: string } }>(
  sorted: readonly T[],
): { userId: string; items: T[] }[] {
  const sections: { userId: string; items: T[] }[] = [];
  for (const item of sorted) {
    const last = sections[sections.length - 1];
    if (last && last.userId === item.author.userId) {
      last.items.push(item);
    } else {
      sections.push({ userId: item.author.userId, items: [item] });
    }
  }
  return sections;
}

/** The part of a comment the sorts read. `MediaComment` satisfies it. */
type Sortable = {
  anchor: MediaCommentAnchor;
  /** Unix ms. */
  createdAt: number;
  author: { userId: string; displayName: string };
};

/**
 * `comments` in the order `sort` names, as a new array — the input is left as it was.
 *
 * - **timecode** — where each comment points, earliest first (`compareByAnchor`).
 * - **oldest / newest** — when the comment was written. Its replies do not count: a two-week-old
 *   comment answered this morning is still two weeks old here. "Latest activity" would be a
 *   different sort, and a thread jumping to the top because someone replied is a surprise in a
 *   list that says it is ordered by age.
 * - **commenter** — the reader's own comments first, then everyone else's grouped by name.
 *   "You" is not sorted as a name: filing the reader's own notes under Y, between two colleagues,
 *   is the one place they would never look. Within each person, timecode order.
 *
 * Every sort breaks its ties with timecode, then creation time, so equal keys never shuffle
 * between renders.
 */
export function sortComments<T extends Sortable>(
  comments: readonly T[],
  sort: CommentSort,
  currentUserId: string | null,
): T[] {
  const byCommenter = (a: T, b: T) => {
    const aOwn = isOwnWriting(a.author, currentUserId);
    const bOwn = isOwnWriting(b.author, currentUserId);
    if (aOwn !== bOwn) return aOwn ? -1 : 1;
    return (
      authorLabel(a.author, currentUserId).localeCompare(
        authorLabel(b.author, currentUserId),
        undefined,
        { sensitivity: 'base' },
      ) ||
      // Two different people can share a name; keep each one's comments together regardless.
      a.author.userId.localeCompare(b.author.userId)
    );
  };

  const primary: Record<CommentSort, (a: T, b: T) => number> = {
    timecode: () => 0,
    oldest: (a, b) => a.createdAt - b.createdAt,
    newest: (a, b) => b.createdAt - a.createdAt,
    commenter: byCommenter,
  };

  return [...comments].sort((a, b) => primary[sort](a, b) || compareByAnchor(a, b));
}
